<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import type { CodeInputVariants } from './variants';
import { computed, onMounted, ref, watch } from 'vue';
import { SphInputSimple } from '@/components/base/input/';
import { codeInputBoxVariants, codeInputVariants } from './variants';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<{
    size?: CodeInputVariants['size']
    length?: number
    autofocus?: boolean
    updateImmediately?: boolean
    inputmode?: InputMode
    modelValue: string | null
    pattern?: string
    disabled?: boolean
}>(), {
    length: 6,
    size: 'normal',
    autofocus: true,
    updateImmediately: false,
    inputmode: 'text',
    pattern: '[a-zA-Z0-9]',
    disabled: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: string | null]
    input: [value: string | null]
}>();

type InputMode = 'decimal' | 'text';

const indexArray = ref([...(Array.from({ length: props.length }).keys())]);
const refs: Array<HTMLInputElement | undefined> = [];

watch(() => props.length, (newLength, oldLength) => {
    const newIndexArray = [...(new Array(newLength).keys())];
    indexArray.value.splice(0, indexArray.value.length, ...newIndexArray);

    if (newLength < oldLength) {
        code.value.splice(newLength);
        for (let i = newLength; i < oldLength; i++) {
            refs[i] = undefined;
        }
        refs.splice(newLength);
    } else {
        for (let i = oldLength; i < newLength; i++) {
            code.value[i] = code.value[i] ?? '';
        }
    }
});

const groupSize = computed(() => {
    if (props.length <= 6) {
        return 0;
    }
    if (props.length % 4 === 0) {
        return 4;
    }
    if (props.length % 3 === 0) {
        return 3;
    }
    if (props.length % 2 === 0) {
        return 2;
    }
    return 0;
});

const groups = computed(() => {
    if (!groupSize.value) {
        return [indexArray.value];
    }
    const result: number[][] = [];
    for (let i = 0; i < indexArray.value.length; i += groupSize.value) {
        result.push(indexArray.value.slice(i, i + groupSize.value));
    }
    return result;
});

let inSyntheticClear = false;

function syncBox(index: number, value: string) {
    if (refs[index] === undefined) {
        return;
    }
    refs[index].value = value;
    code.value[index] = value;
    inSyntheticClear = true;
    refs[index].dispatchEvent(new Event('input', { bubbles: true }));
    inSyntheticClear = false;
}

const code = ref<string[]>(
    props.modelValue ? props.modelValue.split('').slice(0, props.length) : [],
);
const codeString = computed(() => code.value.join('').toUpperCase());
const codeFilled = computed(() => {
    if (code.value.length !== props.length) {
        return false;
    }
    return code.value.every((char) => char.length > 0);
});

let lastEmitted: string | null = props.modelValue ?? null;

watch(() => props.modelValue, (value) => {
    if (value === lastEmitted) {
        return;
    }
    const chars = value ? value.split('').slice(0, props.length) : Array.from<string>({ length: props.length }).fill('');
    indexArray.value.forEach((index) => {
        code.value[index] = chars[index] ?? '';
        syncBox(index, code.value[index]);
    });

    if (props.autofocus && isFocussed()) {
        onFocusIn();
    }
});

function assignRef(component: ComponentPublicInstance | Element | null, index: number) {
    if (component === null) {
        refs[index] = undefined as unknown as HTMLInputElement;
        return;
    }
    refs[index] = (component as ComponentPublicInstance).$el as HTMLInputElement;
}

function onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const content = event.clipboardData?.getData('text/plain');
    if (content !== undefined && content.length === props.length) {
        const pattern = props.pattern ? new RegExp(props.pattern) : null;
        if (pattern && content.split('').some((char) => !pattern.test(char))) {
            return;
        }
        indexArray.value.forEach((index) => {
            syncBox(index, content[index] ?? '');
        });
        refs[props.length - 1]?.focus();
        emitCodeValue();
    }
}

function isFocussed() {
    return refs.includes(document.activeElement as HTMLInputElement);
}

function onFocusIn() {
    const firstEmpty = refs.find((element) => element && element.value.length === 0);
    if (firstEmpty !== undefined) {
        firstEmpty.focus();
    } else {
        refs[props.length - 1]?.focus();
    }
}

function onInput(index: number) {
    if (inSyntheticClear) {
        return;
    }
    if (!refs[index]) {
        return;
    }
    const char = refs[index].value.substring(0, 1);
    if (props.pattern && char && !new RegExp(props.pattern).test(char)) {
        syncBox(index, code.value[index] ?? '');
        return;
    }
    code.value[index] = char;
    refs[index].value = char;

    emit('input', codeString.value || null);

    if (index < props.length - 1) {
        refs[index + 1]?.focus();

        if (props.updateImmediately) {
            emitCodeValue();
        }
    } else if (codeFilled.value) {
        emitCodeValue();
    }
}

function onKeyDown(event: KeyboardEvent, index: number) {
    switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
            event.preventDefault();
            break;
        case 'Backspace':
            event.preventDefault();
            if ((refs[index]?.value.length ?? 0) > 0) {
                // The current box has a character → clear it in place, stay focused here
                syncBox(index, '');
            } else if (index > 0) {
                // Current box is already empty → go back one box, clear that one, and focus it
                syncBox(index - 1, '');
                refs[index - 1]?.focus();
            }

            if (props.updateImmediately) {
                emitCodeValue();
            } else {
                emit('update:modelValue', null);
            }
            break;
        default:
            if (event.key.length === 1 && props.pattern && !new RegExp(props.pattern).test(event.key)) {
                event.preventDefault();
                return;
            }
            return true;
    }
}

function emitCodeValue() {
    const joined: string | null = code.value.join('').toUpperCase() || null;
    lastEmitted = joined;
    emit('update:modelValue', joined);
}

onMounted(() => {
    if (props.autofocus) {
        refs[0]?.focus();
    }
});
</script>

<template>
    <div
        :class="codeInputVariants({
            size: props.size,
        })"
    >
        <div
            v-for="(group, groupIndex) in groups"
            :key="groupIndex"
            class="group"
        >
            <SphInputSimple
                v-for="index in group"
                :key="index"
                :ref="(comp) => assignRef(comp, index)"
                type="text"
                :class="codeInputBoxVariants({ size: props.size })"
                :inputmode="props.inputmode"
                autocomplete="off"
                autocapitalize="characters"
                name="code-input"
                maxlength="1"
                :disabled="props.disabled"
                :pattern="props.pattern"
                @paste.prevent="onPaste"
                @focusin.prevent="onFocusIn"
                @input.prevent="onInput(index)"
                @keydown="onKeyDown($event, index)"
            />
        </div>
    </div>
</template>
